import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const sourceLabel = (s) => ({ invoice: 'Invoice', payment: 'Payment', deposit: 'Deposit', expense: 'Expense', stripe_import: 'Stripe', credit_note: 'Credit note', manual: 'Manual' }[s] || s || '—');

export default function Reconcile() {
  const { activeId } = useBusiness();
  const { user } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [stmtDate, setStmtDate] = useState(today());
  const [endingBal, setEndingBal] = useState('');
  const [ws, setWs] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [history, setHistory] = useState([]);
  const [loadingWs, setLoadingWs] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadAccounts = useCallback(() => {
    api(`/reconciliations/accounts?business_id=${activeId}`)
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setAccounts(list);
        setAccountId((prev) => (list.some((a) => String(a.id) === String(prev)) ? prev : (list[0]?.id ? String(list[0].id) : '')));
      })
      .catch(() => setAccounts([]));
  }, [activeId]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadHistory = useCallback(() => {
    if (!accountId) { setHistory([]); return; }
    api(`/reconciliations?business_id=${activeId}&account_id=${accountId}`).then((d) => setHistory(Array.isArray(d) ? d : [])).catch(() => setHistory([]));
  }, [activeId, accountId]);
  useEffect(() => { loadHistory(); setWs(null); setChecked(new Set()); setNotice(null); setError(null); }, [loadHistory]);

  const acct = accounts.find((a) => String(a.id) === String(accountId));

  async function loadWorksheet() {
    if (!accountId || !stmtDate) return;
    setLoadingWs(true); setError(null); setNotice(null);
    try {
      const d = await api(`/reconciliations/worksheet?business_id=${activeId}&account_id=${accountId}&statement_date=${stmtDate}`);
      setWs(d); setChecked(new Set());
    } catch (e) { setError(e?.detail || 'Could not load transactions.'); setWs(null); }
    finally { setLoadingWs(false); }
  }

  const toggle = (id) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = ws && ws.lines.length > 0 && ws.lines.every((l) => checked.has(l.id));
  const toggleAll = () => setChecked(() => (allChecked ? new Set() : new Set((ws?.lines || []).map((l) => l.id))));

  const clearedSelected = useMemo(() => (ws?.lines || []).filter((l) => checked.has(l.id)).reduce((s, l) => s + Number(l.amount), 0), [ws, checked]);
  const beginning = Number(ws?.beginning_balance || 0);
  const clearedBalance = beginning + clearedSelected;
  const endingNum = endingBal === '' ? null : Number(endingBal);
  const difference = endingNum == null ? null : endingNum - clearedBalance;
  const balanced = ws && endingNum != null && Math.abs(difference) < 0.005;

  async function finish() {
    if (!balanced) return;
    setFinishing(true); setError(null); setNotice(null);
    try {
      const r = await api('/reconciliations/complete', {
        method: 'POST',
        body: { business_id: activeId, account_id: Number(accountId), statement_date: stmtDate, ending_balance: endingNum, line_ids: [...checked] },
      });
      setNotice(`Reconciled ${r.cleared_count} transaction${r.cleared_count === 1 ? '' : 's'} as of ${date(stmtDate)}.`);
      setWs(null); setChecked(new Set()); setEndingBal('');
      loadAccounts(); loadHistory();
    } catch (e) {
      if (e?.detail === 'out_of_balance') setError('Out of balance — the cleared total doesn’t match the statement. Check your selections and ending balance.');
      else if (e?.detail === 'stale_lines') { setError('The transaction list changed — reloading.'); loadWorksheet(); }
      else setError(e?.detail || 'Could not finish the reconciliation.');
    } finally { setFinishing(false); }
  }

  async function undo(id) {
    if (!window.confirm('Undo this reconciliation? Its transactions will become unreconciled again.')) return;
    try { await api(`/reconciliations/${id}`, { method: 'DELETE' }); loadAccounts(); loadHistory(); }
    catch (e) { setError(e?.detail || 'Could not undo that reconciliation.'); }
  }

  return (
    <>
      <PageHeader title="Bank reconciliation" subtitle={acct ? `${acct.code} · ${acct.name}` : undefined} />

      {accounts.length === 0 ? (
        <section className="panel panel--pad"><p className="hint">No bank accounts found for this business. Mark an account as a bank account in the chart of accounts to reconcile it.</p></section>
      ) : (
        <>
          <section className="panel panel--pad" style={{ maxWidth: 720 }}>
            <div className="form-grid">
              <div className="field"><label>Bank account</label>
                <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
              </div>
              <div className="field"><label>Statement ending date</label>
                <input type="date" value={stmtDate} onChange={(e) => setStmtDate(e.target.value)} /></div>
              <div className="field"><label>Statement ending balance</label>
                <input type="number" step="0.01" value={endingBal} onChange={(e) => setEndingBal(e.target.value)} placeholder="0.00" /></div>
            </div>
            {acct && (
              <p className="hint" style={{ marginBottom: 0 }}>
                Book balance {money(acct.book_balance)} · Reconciled {money(acct.reconciled_balance)}
                {acct.last_statement_date ? ` · Last reconciled ${date(acct.last_statement_date)} at ${money(acct.last_ending_balance)}` : ' · Never reconciled'}
              </p>
            )}
            <div style={{ marginTop: 14 }}>
              <button className="btn btn--primary" onClick={loadWorksheet} disabled={loadingWs || !accountId}>
                {loadingWs ? 'Loading…' : 'Load transactions'}
              </button>
            </div>
            {notice && <div className="form-note" style={{ marginTop: 14 }}>{notice}</div>}
            {error && <div className="form-error" style={{ marginTop: 14 }}>{error}</div>}
          </section>

          {ws && (
            <>
              <section className="panel">
                <div className="panel__head">
                  <h2 className="panel__title">Uncleared transactions through {date(stmtDate)}</h2>
                  <span className="hint">{checked.size} of {ws.lines.length} selected</span>
                </div>
                {ws.lines.length === 0 ? (
                  <p className="hint" style={{ padding: '0 16px 16px' }}>Nothing to clear in this period.</p>
                ) : (
                  <table className="recon-table">
                    <thead>
                      <tr>
                        <th className="recon-ck"><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                        <th>Date</th><th>Description</th><th>Type</th><th className="recon-amt">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ws.lines.map((l) => (
                        <tr key={l.id} className={checked.has(l.id) ? 'is-checked' : ''} onClick={() => toggle(l.id)}>
                          <td className="recon-ck"><input type="checkbox" checked={checked.has(l.id)} onChange={() => toggle(l.id)} onClick={(e) => e.stopPropagation()} /></td>
                          <td>{date(l.entry_date)}</td>
                          <td>{l.memo || '—'}</td>
                          <td>{sourceLabel(l.source_type)}</td>
                          <td className={`recon-amt num ${Number(l.amount) < 0 ? 'neg' : 'pos'}`}>{money(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="panel panel--pad recon-summary" style={{ maxWidth: 720 }}>
                <div className="recon-sum-row"><span>Beginning (reconciled) balance</span><span className="num">{money(beginning)}</span></div>
                <div className="recon-sum-row"><span>Cleared this statement</span><span className="num">{money(clearedSelected)}</span></div>
                <div className="recon-sum-row recon-sum-row--total"><span>Cleared balance</span><span className="num">{money(clearedBalance)}</span></div>
                <div className="recon-sum-row"><span>Statement ending balance</span><span className="num">{endingNum == null ? '—' : money(endingNum)}</span></div>
                <div className={`recon-sum-row recon-sum-row--diff ${balanced ? 'ok' : 'bad'}`}>
                  <span>Difference</span><span className="num">{difference == null ? '—' : money(difference)}</span>
                </div>
                <button className="btn btn--primary" style={{ marginTop: 12 }} onClick={finish} disabled={!balanced || finishing}>
                  {finishing ? 'Finishing…' : balanced ? 'Finish reconciliation' : 'Balance to finish'}
                </button>
              </section>
            </>
          )}

          {history.length > 0 && (
            <section className="panel">
              <div className="panel__head"><h2 className="panel__title">Reconciliation history</h2></div>
              <table className="recon-table">
                <thead><tr><th>Statement date</th><th className="recon-amt">Beginning</th><th className="recon-amt">Ending</th><th>Items</th><th /></tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{date(h.statement_date)}</td>
                      <td className="recon-amt num">{money(h.beginning_balance)}</td>
                      <td className="recon-amt num">{money(h.ending_balance)}</td>
                      <td>{h.line_count}</td>
                      <td className="recon-amt">{user?.role === 'admin' && <button className="btn btn--sm btn--danger" onClick={() => undo(h.id)}>Undo</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </>
  );
}
